import { CLUSTER_CRUD, COUNTRIES_CRUD, PLATFORM_CRUD } from '../Constants/ApiDefinations'
import HTTP_STATUS_CODE from '../Constants/HttpStatusCode'
import mainApiService from '../Services'
import { PaginatedFetchResult } from '../Hooks/useApiPaginatedOptions'

export interface FilterOption {
  key: string
  value: string
}

/** Builds the `searchText=a&page=1&limit=10` style query for the list APIs. */
const buildListQuery = (page: number, limit: number, searchText: string): string => {
  const params = new URLSearchParams()
  if (searchText?.trim()) {
    params.set('searchText', searchText.trim())
  }
  params.set('page', String(page))
  params.set('limit', String(limit))
  return params.toString()
}

type ListApiDefinition = (query: string) => {
  url: string
  method: any
  data: any
}

/**
 * Generic paginated option fetcher for the listing filter dropdowns. Calls the
 * provided list API with `?searchText=&page=&limit=`, forwards the abort signal,
 * and normalises the response into `{ key, value }` options + total count so it
 * can be consumed directly by `useApiPaginatedOptions`.
 */
const fetchFilterOptions = async (
  apiDefinition: ListApiDefinition,
  page: number,
  limit: number,
  searchText: string,
  signal: AbortSignal,
): Promise<PaginatedFetchResult<FilterOption>> => {
  const requestedData = apiDefinition(buildListQuery(page, limit, searchText))
  const result: any = await mainApiService({ ...requestedData, signal })

  if (result?.responseCode !== HTTP_STATUS_CODE.OK) {
    if (result?.responseCode === HTTP_STATUS_CODE.NOT_FOUND) {
      return { data: [], total: 0 }
    }
    throw new Error(result?.message ?? 'Failed to fetch filter options')
  }

  const records: any[] = result?.data?.records ?? []
  const data: FilterOption[] = records.map((ele: any) => ({
    key: ele?._id,
    value: ele?.value,
  }))

  return { data, total: result?.data?.count ?? data.length }
}

export const fetchClusterOptions = (
  page: number,
  limit: number,
  searchText: string,
  signal: AbortSignal,
): Promise<PaginatedFetchResult<FilterOption>> =>
  fetchFilterOptions(CLUSTER_CRUD.LIST_CLUSTER, page, limit, searchText, signal)

export const fetchPlatformOptions = (
  page: number,
  limit: number,
  searchText: string,
  signal: AbortSignal,
): Promise<PaginatedFetchResult<FilterOption>> =>
  fetchFilterOptions(PLATFORM_CRUD.LIST_PLATFORM, page, limit, searchText, signal)

export const fetchCountryOptions = (
  page: number,
  limit: number,
  searchText: string,
  signal: AbortSignal,
): Promise<PaginatedFetchResult<FilterOption>> =>
  fetchFilterOptions(COUNTRIES_CRUD.LIST_COUNTRY, page, limit, searchText, signal)

export const getOptionId = (option: FilterOption): string => option.key
